import { useState, useEffect } from 'react';
import {
  Eye, Activity, Shield, Zap, Cloud,
  RefreshCw, AlertOctagon, CheckCircle, XCircle,
  Save
} from 'lucide-react';
import { useFeatureFlags } from '../../hooks/useFeatureFlags';

interface GlobalDefaultsTabProps {
  onRefresh: () => void;
}

export default function GlobalDefaultsTab({ onRefresh }: GlobalDefaultsTabProps) {
  const { globalFlags, loading, error, updateGlobalFlags } = useFeatureFlags();
  const [localFlags, setLocalFlags] = useState<any>(globalFlags);
  const [hasChanges, setHasChanges] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setLocalFlags(globalFlags);
  }, [globalFlags]);

  const handleToggle = (flagName: string, value: any) => {
    setLocalFlags((prev: any) => ({
      ...prev,
      [flagName]: value,
    }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateGlobalFlags(localFlags);
      setHasChanges(false);
      onRefresh();
    } catch (err) {
      console.error('Failed to save flags:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleDiscard = () => {
    setLocalFlags(globalFlags);
    setHasChanges(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-6">
        <div className="flex items-center gap-3">
          <AlertOctagon className="w-6 h-6 text-red-400" />
          <div>
            <h3 className="text-lg font-semibold text-red-400">Error Loading Flags</h3>
            <p className="text-slate-400 mt-1">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  const featureCategories = [
    {
      title: 'Observability (Phase 0)',
      icon: Eye,
      color: 'from-blue-600 to-cyan-600',
      flags: [
        { key: 'enableOpenTelemetry', label: 'OpenTelemetry Tracing', description: 'Distributed tracing for end-to-end visibility' },
        { key: 'enableDetailedMetrics', label: 'Detailed Metrics', description: 'Counter and histogram metrics collection' },
        { key: 'enablePerformanceLogging', label: 'Performance Logging', description: 'Console logs for operation timing' },
        { key: 'traceSampleRate', label: 'Trace Sample Rate', description: 'Percentage of traces to capture (0.0 to 1.0)', type: 'number', min: 0, max: 1, step: 0.1 },
      ],
    },
    {
      title: 'Caching (Phase 1)',
      icon: Zap,
      color: 'from-green-600 to-teal-600',
      flags: [
        { key: 'enableRedisCache', label: 'Redis Cache', description: 'Enable Redis caching for dashboard queries' },
        { key: 'redisCacheTTL', label: 'Cache TTL (seconds)', description: 'Time-to-live for cached data', type: 'number', min: 60, max: 7200, step: 60 },
        { key: 'cacheCurrentMonth', label: 'Cache Current Month', description: 'More aggressive caching for current month' },
      ],
    },
    {
      title: 'Reliability (Phase 2)',
      icon: Shield,
      color: 'from-orange-600 to-red-600',
      flags: [
        { key: 'enableCircuitBreaker', label: 'Circuit Breaker', description: 'Protect against cascading failures' },
        { key: 'circuitBreakerThreshold', label: 'Circuit Breaker Threshold', description: 'Consecutive failures before opening circuit', type: 'number', min: 1, max: 20, step: 1 },
        { key: 'enableReconciliation', label: 'Daily Reconciliation', description: 'Verify aggregate data matches raw data' },
        { key: 'reconciliationTolerance', label: 'Reconciliation Tolerance ($)', description: 'Dollar amount tolerance for discrepancies', type: 'number', min: 0.01, max: 10, step: 0.01 },
        { key: 'autoFixReconciliation', label: 'Auto-Fix Reconciliation', description: 'Automatically trigger re-aggregation on discrepancies' },
      ],
    },
    {
      title: 'Scalability (Phase 3)',
      icon: Cloud,
      color: 'from-purple-600 to-pink-600',
      flags: [
        { key: 'enableCloudTasksFanout', label: 'Cloud Tasks Fan-Out', description: 'Parallel processing for large uploads' },
        { key: 'fanoutThreshold', label: 'Fan-Out Threshold', description: 'Minimum records to trigger fan-out', type: 'number', min: 1000, max: 50000, step: 1000 },
        { key: 'enableBigQueryExport', label: 'BigQuery Export', description: 'Export aggregates to BigQuery for analytics' },
        { key: 'bigQueryDataset', label: 'BigQuery Dataset', description: 'Dataset name for exports', type: 'text' },
        { key: 'enableStreamingAggregation', label: 'Streaming Aggregation', description: 'Real-time streaming aggregation (future)' },
      ],
    },
    {
      title: 'Performance Tuning',
      icon: Activity,
      color: 'from-indigo-600 to-purple-600',
      flags: [
        { key: 'maxBatchSize', label: 'Max Batch Size', description: 'Maximum records per batch', type: 'number', min: 100, max: 5000, step: 100 },
        { key: 'firestoreTimeout', label: 'Firestore Timeout (ms)', description: 'Query timeout in milliseconds', type: 'number', min: 5000, max: 120000, step: 5000 },
        { key: 'enableParallelAggregation', label: 'Parallel Aggregation', description: 'Process multiple months in parallel' },
      ],
    },
  ];

  return (
    <div className="space-y-6">
      {/* Action Bar */}
      {hasChanges && (
        <div className="sticky top-0 z-10 bg-gradient-to-r from-purple-600 to-pink-600 rounded-xl p-4 shadow-2xl border border-purple-500/50">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <AlertOctagon className="w-5 h-5 text-white" />
              <div>
                <p className="text-white font-semibold">Unsaved Changes</p>
                <p className="text-white/70 text-sm">You have modified global feature flags</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={handleDiscard}
                className="px-4 py-2 bg-white/10 hover:bg-white/20 text-white font-medium rounded-lg transition-all"
              >
                Discard
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="inline-flex items-center px-6 py-2 bg-white text-purple-600 font-semibold rounded-lg hover:bg-white/90 transition-all shadow-lg disabled:opacity-50"
              >
                {saving ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Save Global Defaults
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Feature Categories */}
      {featureCategories.map((category) => {
        const Icon = category.icon;
        return (
          <div
            key={category.title}
            className="relative overflow-hidden rounded-xl bg-slate-800/50 backdrop-blur-sm border border-slate-700/50"
          >
            {/* Category Header */}
            <div className={`px-6 py-4 bg-gradient-to-r ${category.color} bg-opacity-10 border-b border-slate-700/50`}>
              <div className="flex items-center gap-3">
                <div className={`p-2 bg-gradient-to-br ${category.color} rounded-lg`}>
                  <Icon className="w-5 h-5 text-white" />
                </div>
                <h3 className="text-xl font-semibold text-white">{category.title}</h3>
              </div>
            </div>

            {/* Flags List */}
            <div className="p-6 space-y-4">
              {category.flags.map((flag) => (
                <div
                  key={flag.key}
                  className="flex items-center justify-between p-4 bg-slate-900/50 rounded-lg border border-slate-700/30 hover:border-slate-600/50 transition-all"
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-3">
                      <h4 className="text-white font-medium">{flag.label}</h4>
                      {localFlags[flag.key] === true && (
                        <span className="px-2 py-1 bg-green-500/20 text-green-400 text-xs font-medium rounded border border-green-500/30">
                          ENABLED
                        </span>
                      )}
                      {localFlags[flag.key] === false && (
                        <span className="px-2 py-1 bg-slate-500/20 text-slate-400 text-xs font-medium rounded border border-slate-500/30">
                          DISABLED
                        </span>
                      )}
                    </div>
                    <p className="text-slate-400 text-sm mt-1">{flag.description}</p>
                  </div>

                  <div className="ml-4">
                    {flag.type === 'number' ? (
                      <input
                        type="number"
                        value={localFlags[flag.key] || flag.min || 0}
                        onChange={(e) => handleToggle(flag.key, parseFloat(e.target.value))}
                        min={flag.min}
                        max={flag.max}
                        step={flag.step}
                        className="w-24 px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white focus:border-purple-500 focus:outline-none"
                      />
                    ) : flag.type === 'text' ? (
                      <input
                        type="text"
                        value={localFlags[flag.key] || ''}
                        onChange={(e) => handleToggle(flag.key, e.target.value)}
                        className="w-48 px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white focus:border-purple-500 focus:outline-none"
                        placeholder="Enter value..."
                      />
                    ) : (
                      <button
                        onClick={() => handleToggle(flag.key, !localFlags[flag.key])}
                        className={`relative inline-flex h-8 w-14 items-center rounded-full transition-colors ${
                          localFlags[flag.key]
                            ? 'bg-gradient-to-r from-green-600 to-teal-600'
                            : 'bg-slate-700'
                        }`}
                      >
                        <span
                          className={`inline-block h-6 w-6 transform rounded-full bg-white transition-transform ${
                            localFlags[flag.key] ? 'translate-x-7' : 'translate-x-1'
                          }`}
                        />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })}

      {/* Emergency Disable All */}
      <div className="relative overflow-hidden rounded-xl bg-red-500/10 backdrop-blur-sm border border-red-500/30 p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-red-600 rounded-xl">
              <AlertOctagon className="w-6 h-6 text-white" />
            </div>
            <div>
              <h3 className="text-xl font-semibold text-red-400">Emergency Rollback</h3>
              <p className="text-slate-400 mt-1">
                Instantly disable all features globally (affects all companies)
              </p>
            </div>
          </div>
          <button
            onClick={() => {
              if (window.confirm('⚠️  This will disable ALL features for ALL companies. Continue?')) {
                const allDisabled = Object.keys(localFlags).reduce((acc: any, key) => {
                  if (typeof localFlags[key] === 'boolean') {
                    acc[key] = false;
                  } else {
                    acc[key] = localFlags[key]; // Keep non-boolean values
                  }
                  return acc;
                }, {} as any);
                setLocalFlags(allDisabled);
                setHasChanges(true);
              }
            }}
            className="px-6 py-3 bg-red-600 hover:bg-red-700 text-white font-semibold rounded-lg transition-all shadow-lg"
          >
            <XCircle className="w-5 h-5 inline mr-2" />
            Disable All Features
          </button>
        </div>
      </div>
    </div>
  );
}
